// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused". It restores the
// Rematch model-cluster init order the pre-carve `@/pages/api/scribeAPIV2`
// import performed as a side effect; the sibling carves on this door measured
// eleven suites dying without it. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useQueryIntegrationOnboardingSessions } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import { IntegrationDesktopClientMap } from '@/integrations/desktop';
import { hasGetScheduleMixin } from '@/integrations/desktop/mixins/GetScheduleMixin';
import { isTauri } from '@/tauri/utils';
import { formatToDateOnly } from '@/utils/dates';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
import {
  GET_INTEGRATIONS_SCHEDULE,
  type GetIntegrationsScheduleParams,
  type GetIntegrationsScheduleType,
} from '@heidi/integrations/ehr/contracts';
import {
  getEhrConnectionErrorMessage,
  useQueryIntegrationsMixinGetSchedule as useQueryIntegrationsMixinGetScheduleCore,
} from '@heidi/integrations/ehr/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { type EHRProvider, OnboardingSessionStatus } from '@heidi/schema/ml-scribe';
import { scheduleDateAtom } from '@heidi/session/data';
import { getDefaultStore } from 'jotai';

export type UseQueryIntegrationsMixinGetScheduleParams<
  TSelected = GetIntegrationsScheduleType,
> = Partial<GetIntegrationsScheduleParams> & {
  filter?: string[];
  enabled?: boolean;
  select?: (data: GetIntegrationsScheduleType) => TSelected;
};

export const invalidateIntegrationsMixinGetScheduleQuery = () => {
  const jotaiStore = getDefaultStore();
  const scheduleDate = jotaiStore.get(scheduleDateAtom);
  const formattedScheduleDate =
    scheduleDate && !isNaN(scheduleDate.getTime())
      ? (formatToDateOnly(scheduleDate) ?? undefined)
      : undefined;

  if (formattedScheduleDate) {
    void reactQueryClient.invalidateQueries({
      queryKey: [
        GET_INTEGRATIONS_SCHEDULE,
        { start_date: formattedScheduleDate, end_date: formattedScheduleDate },
      ],
    });
  }

  // The appointment date range filter keys its queries on its own range, not
  // on `scheduleDate`; refresh whichever of those is on screen.
  void reactQueryClient.invalidateQueries({
    queryKey: [GET_INTEGRATIONS_SCHEDULE],
    type: 'active',
    predicate: (query) => {
      const params = query.queryKey[1] as
        | { start_date?: string; end_date?: string }
        | undefined;
      if (!params?.start_date || !params.end_date) return false;
      return (
        params.start_date !== formattedScheduleDate ||
        params.end_date !== formattedScheduleDate
      );
    },
  });
};

export function useQueryIntegrationsMixinGetSchedule<
  TSelected = GetIntegrationsScheduleType,
>({
  start_date,
  end_date,
  filter,
  enabled = true,
  select,
}: UseQueryIntegrationsMixinGetScheduleParams<TSelected>) {
  const { t } = useTranslations();
  const {
    integrationEhrProvider,
    isIntegrationOnboardingSession,
    hasMultiple,
    canSyncSchedule,
  } = useIntegrationEhrInfo();

  const { data: integrationOnboardingSession } = useQueryIntegrationOnboardingSessions({
    params: {
      status: OnboardingSessionStatus.completed,
    },
    select: (data) =>
      data.sessions.find(
        (session) => (session.provider as EHRProvider) === integrationEhrProvider
      ),
  });

  return useQueryIntegrationsMixinGetScheduleCore({
    start_date,
    end_date,
    filter,
    enabled,
    select,
    integrationEhrProvider,
    isIntegrationOnboardingSession,
    canSyncSchedule,
    hasMultiple,
    integrationOnboardingSession,
    fetchFromDesktop: async ({
      start_date: startDate,
      end_date: endDate,
      integrationEhrProvider: provider,
    }) => {
      const integrationDesktopClient =
        IntegrationDesktopClientMap[provider as EHRProvider];
      if (integrationDesktopClient) {
        // Critical: this is a workaround to allow the e2e tests to run in the browser by mocking the tauri internals
        const hasTauriInvoke =
          typeof window !== 'undefined' && Boolean(window.__TAURI_INTERNALS__?.invoke);
        // Check hasTauriInvoke first: if the e2e shim is active, skip isTauri() entirely.
        // isTauri() throws in non-prod when __TAURI_INTERNALS__ is set without NEXT_PUBLIC_IS_TAURI,
        // which the shim triggers.
        if (!hasTauriInvoke && !isTauri()) {
          return { handled: true, schedule: [] };
        }

        if (hasGetScheduleMixin(integrationDesktopClient)) {
          if (!integrationOnboardingSession) {
            return { handled: false };
          }
          return {
            handled: true,
            schedule: await integrationDesktopClient.getSchedule({
              startDate,
              endDate,
              integrationOnboardingSession,
            }),
          };
        }

        toast({
          title: 'Error',
          description:
            'This integration: {integrationEhrProvider} does not support getSchedule',
          tone: 'destructive',
        });
      }

      return { handled: false };
    },
    onConnectionError: (error) => {
      toast({
        tone: 'destructive',
        title: t('ehr.ehrConnectionError'),
        description: getEhrConnectionErrorMessage(error),
      });
    },
  });
}
